import React, { useEffect, useRef } from 'react';

import ParameterSet, { ParameterGroupProvider } from 'sharedreact/components/ParameterSet';
import SegmentedButtonField from 'sharedreact/components/SegmentedButtonField';
import TextField from 'sharedreact/components/TextField';
import hideable from 'sharedreact/components/hideable';
import { translate } from 'sharedreact/utils/LanguageSupport';
import ValidationRules from 'sharedreact/utils/ValidationRules';

import OpenIDAzureConfigPanel from 'workspaces/launcher/components/admin/OpenIDAzureConfigPanel';
import OpenIDOktaConfigPanel from 'workspaces/launcher/components/admin/OpenIDOktaConfigPanel';
import OpenIDConfig from 'workspaces/launcher/data/OpenIDConfig';
import { SegmentType } from 'sharedreact/components/SegmentedButton';
import Dialog from 'sharedreact/components/Dialog';
import ValidationForm, { useIsFormValid, ValidationProps, ValidationProvider, ValidationWrapper } from 'sharedreact/components/ValidationForm';

type Props = {
  show: boolean,
  language: string,
  openIDConfig: OpenIDConfig | null,

  onClose: () => void,
  onSave: (openIdConfig: OpenIDConfig) => void,
  onChangeValue: (name: string, value: any) => void
} & ValidationProps;

type State = {
  isDirty: boolean,
  isOktaFormValid: boolean,
  isAzureFormValid: boolean
};

function _displayInvalidName (): string {
  return translate('launcher.identityproviders.oidcconfig.err.illegalname');
}

function OpenIDOktaConfigPanelWithValidation ({
  openIDConfig, onChangeValue, setIsFormValid
} : {
  openIDConfig: OpenIDConfig;
  onChangeValue: (name: string, value: any) => void;
  setIsFormValid: (isFormValid: boolean) => void;
}) {
  const [ isFormValid ] = useIsFormValid();

  useEffect(() => {
    setIsFormValid(isFormValid);
  }, [setIsFormValid, isFormValid]);

  return (
    <OpenIDOktaConfigPanel
      openIDConfig={openIDConfig}
      onChangeValue={onChangeValue}
      isFormValid={isFormValid}
    />
  );
}

function OpenIDAzureConfigPanelWithValidation ({
  openIDConfig, onChangeValue, setIsFormValid
} : {
  openIDConfig: OpenIDConfig;
  onChangeValue: (name: string, value: any) => void;
  setIsFormValid: (isFormValid: boolean) => void;
}) {
  const [ isFormValid ] = useIsFormValid();

  useEffect(() => {
    setIsFormValid(isFormValid);
  }, [setIsFormValid, isFormValid]);

  return (
    <OpenIDAzureConfigPanel
      openIDConfig={openIDConfig}
      onChangeValue={onChangeValue}
      isFormValid={isFormValid}
    />
  );
}

class EditOpenIDConfigDialogImpl extends React.Component<Props, State> {  
  private _edited: string[];
  private _generalRequired: string[];
  private _oktaRequired: string[];
  private _azureRequired: string[];


  constructor (props: Props) {
    super(props);

    this.state = {
      isDirty: false,
      isOktaFormValid: true,
      isAzureFormValid: true
    };

    this._edited = [];
    this._generalRequired = ['name'];
    this._oktaRequired = ['oktahost', 'clientId', 'clientSecret'];
    this._azureRequired = ['tenantId', 'clientId', 'clientSecret'];

    this.handleRequiredChange = this.handleRequiredChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    
    this.handleSetIsOktaFormValid = this.handleSetIsOktaFormValid.bind(this);
    this.handleSetIsAzureFormValid = this.handleSetIsAzureFormValid.bind(this);
  }

  override componentDidMount (): void {
    if (this.props.openIDConfig != null) {
      const openIDConfig: OpenIDConfig = this.props.openIDConfig;
      this._edited = [];

      if (openIDConfig.name !== '') {
        this._edited.push('name');
      }
      if (openIDConfig.clientId !== '') {
        this._edited.push('clientId');
      }
      if (openIDConfig.clientSecret !== '' ||
        openIDConfig.id !== '' // id !== '' means this is a saved config and
                               // and should always have a clientSecret
        ) {
        this._edited.push('clientSecret');
      }
      if (openIDConfig.oktahost !== '') {
        this._edited.push('oktahost');
      }
      if (openIDConfig.tenantId !== '') {
        this._edited.push('tenantId');
      }
    }
  }

  clearDirty (): void {
    this.setState({'isDirty': false});
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if (this.props.openIDConfig != null && prevProps.openIDConfig == null) {
      const openIDConfig: OpenIDConfig = this.props.openIDConfig;
      this._edited = [];
      this.clearDirty();

      if (openIDConfig.name !== '') {
        this._edited.push('name');
      }
      if (openIDConfig.clientId) {
        this._edited.push('clientId');
      }
      if (openIDConfig.clientSecret !== '' ||
        openIDConfig.id !== '' // id !== '' means this is a saved config and
                              // and should always have a clientSecret
      ) {
        this._edited.push('clientSecret');
      }
      if (openIDConfig.oktahost !== '') {
        this._edited.push('oktahost');
      }
      if (openIDConfig.tenantId !== '') {
        this._edited.push('tenantId');
      }
    }
  }

  handleRequiredChange (name: string, value: any): void {
    if (!this._edited.includes(name)) {
      this._edited.push(name);
    }

    if (!this.state.isDirty) {
      this.setState({isDirty: true});
    }

    this.props.onChangeValue(name, value);
  }

  handleChange (name: string, value: any): void {
    if (!this.state.isDirty) {
      this.setState({isDirty: true});
    }

    this.props.onChangeValue(name, value);
  }

  handleSubmit () {
    if (this.props.openIDConfig != null) {
      const openIDConfig: OpenIDConfig = this.props.openIDConfig;
    
      let isSubformValid: boolean = false;
      const isOkta: boolean = openIDConfig.isOkta();
      if (isOkta) {
        isSubformValid = this.state.isOktaFormValid;
      } else {
        isSubformValid = this.state.isAzureFormValid;
      }

      if (this.props.isFormValid && isSubformValid) {
        this.props.onSave(openIDConfig);
        this.props.onClose();
      }
    }
  }

  handleSetIsOktaFormValid (isFormValid: boolean) {
    this.setState({isOktaFormValid: isFormValid});
  }

  handleSetIsAzureFormValid (isFormValid: boolean) {
    this.setState({isAzureFormValid: isFormValid});
  }

  override render (): React.ReactNode {
    const { show, openIDConfig, onClose, language, isFormValid } = this.props;

    if (openIDConfig == null) {
      return null;
    }

    const isOkta: boolean = openIDConfig.isOkta();

    const allElementsEdited: boolean = (
      this._edited.filter(name => this._generalRequired.includes(name)).length === this._generalRequired.length &&
      (isOkta
        ? this._edited.filter(name => this._oktaRequired.includes(name)).length === this._oktaRequired.length
        : this._edited.filter(name => this._azureRequired.includes(name)).length === this._azureRequired.length
      )
    );

    const hasFormErrors = !isFormValid;
    const hasOktaErrors = !this.state.isOktaFormValid;
    const hasAzureErrors = !this.state.isAzureFormValid;

    const okDisabled: boolean = (
      !this.state.isDirty ||
      !allElementsEdited ||
      hasFormErrors ||
      (isOkta
        ? hasOktaErrors
        : hasAzureErrors
      )
    );

    const submit = (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      this.handleSubmit();
    };

    const segmentsType: SegmentType[] = [
      { id: 'azure',
        text: translate('launcher.identityproviders.oidc.azure')
      },
      { id: 'okta',
        text: translate('launcher.identityproviders.oidc.okta')
      }
    ];

    const nameRule = ValidationRules.addCustomRule('input_openIDName', (value) => !value.match(/[ #%&|\\*?=:/@'"`{}[\]<>()]/), _displayInvalidName);
    const nameValidations = ['required'].concat(nameRule);

    const isNew: boolean = openIDConfig.isNew();

    return (
      <Dialog
        id='sa_dialog_openidconfig'
        show={show}
        language={language}
        title={translate(isNew ? 'launcher.identityproviders.oidcconfig.addtitle' : 'launcher.identityproviders.oidcconfig.edittitle')}
        onClose={onClose}
        onConfirm={submit}
        disableConfirm={okDisabled}
        primaryButton='confirm'
      >
        <ParameterGroupProvider>
          <ValidationForm>
            <ParameterSet useProvider={false}>
              <TextField id='ae_text_identityproviders_openid_name'
                name='name'
                value={openIDConfig.name}
                label={translate('launcher.identityproviders.oidc.name')}
                onChangeValue={this.handleRequiredChange}
                validations={nameValidations}
              />
              <SegmentedButtonField id='ae_segment_identityproviders_openid_type'
                name='type'
                className='esko_parameter-input-group-max500'
                value={openIDConfig.type}
                label={translate('launcher.identityproviders.oidc.type')}
                segments={segmentsType}
                onChangeValue={this.handleChange}
              />
            </ParameterSet>
          </ValidationForm>
          {isOkta
          ? (
            <ValidationProvider>
              <OpenIDOktaConfigPanelWithValidation
                openIDConfig={openIDConfig}
                onChangeValue={this.handleRequiredChange}
                setIsFormValid={this.handleSetIsOktaFormValid}
              />
            </ValidationProvider>
          )
          : (
            <ValidationProvider>
              <OpenIDAzureConfigPanelWithValidation
                openIDConfig={openIDConfig}
                onChangeValue={this.handleRequiredChange}
                setIsFormValid={this.handleSetIsAzureFormValid}
              />
            </ValidationProvider>
          )}
        </ParameterGroupProvider>
      </Dialog>
    );
  }
}

const EditOpenIDConfigDialog = ValidationWrapper(EditOpenIDConfigDialogImpl);

export default hideable(EditOpenIDConfigDialog);
